/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeBreakpointContext,
  BaSeLoadingPage,
  BaSeStepper,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';
import { useAnswerController } from '../../../../hooks/controllers/answer-controller';
import { useInterviewController } from '../../../../hooks/controllers/interview-controller';
import { useModuleController } from '../../../../hooks/controllers/module-public-controller';
import {
  MapReponserMethod,
  useParamSystemController,
} from '../../../../hooks/controllers/param-system-controller';
import { BasicData } from '../../components/basic-data/basic-data';
import { LayoutCandidate } from '../../components/layout-candidate';
import { MeetEmpretec } from '../../components/meet-empretec/meet-empretec';
import QuestionSection from '../../components/question-selection/question-selection';
import { Chat } from '../../components/selection-interview/chat/chat';
import { SelectionInterview } from '../../components/selection-interview/selection-interview';
import { ContainerAboutCourse } from './about-course-styled';

export const isFinalizedStateMap = {
  NAO_PREENCHIDO: false,
  EM_PREENCHIMENTO: false,
  FINALIZADA: true,
  EXPIRADA: true,
  REENTREVISTA: false,
  EM_REVISAO: false,
  CERTIFICADA: true,
};

export const AboutCourse = () => {
  const { hasRole } = Sebrae.useAuth();
  const breakpointController = React.useContext(BaSeBreakpointContext);
  const { navigateTo, getRouteParam } = Sebrae.useSebraeRouter();
  const { findInterviewByUserAuth, findInterviewByAdministratorAuth } =
    useInterviewController();
  const { getAnswersByInterview } = useAnswerController();
  const { findRequestMethodByInterview } = useParamSystemController();

  const { getSettingsModule } = useModuleController();

  const [currentStep, setCurrentStep] = React.useState(1);
  const [interview, setInterview] = React.useState();
  const [answers, setAnswers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [settingsModule, setSettingsModule] = React.useState();
  const [responserMethod, setResponserMethod] = React.useState();

  const setModulesToConfig = React.useCallback(
    (id) => {
      getSettingsModule(id)
        .then((settings) => {
          setSettingsModule(settings);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [getSettingsModule],
  );

  React.useEffect(() => {
    const fetchInterviewData = async () => {
      setLoading(true);
      try {
        const idInterview = getRouteParam('id');
        let interviewData;

        if (idInterview) {
          interviewData = await findInterviewByAdministratorAuth(
            Number(idInterview),
          );
        } else {
          interviewData = await findInterviewByUserAuth();
        }

        if (!interviewData) {
          navigateTo('/candidato');
          return;
        }

        setInterview(interviewData);
        setModulesToConfig(interviewData.module.id);

        if (interviewData.statusInterview !== 'SEM_RESULTADO') {
          setCurrentStep(3);
        }

        const answersList = await getAnswersByInterview(interviewData.id);
        if (answersList.length > 0) {
          setAnswers(answersList);
        }

        const responserMethodData = await findRequestMethodByInterview(
          interviewData.id,
        );
        setResponserMethod(responserMethodData);

        setStep(
          interviewData,
          getAnswersByInterview,
          setCurrentStep,
          setAnswers,
          navigateTo,
          hasRole,
        );
      } catch (error) {
        console.log(error);
        navigateTo('/candidato');
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewData();
  }, [
    findInterviewByAdministratorAuth,
    findInterviewByUserAuth,
    getAnswersByInterview,
    getRouteParam,
    findRequestMethodByInterview,
    navigateTo,
    hasRole,
    setModulesToConfig,
  ]);

  const pageSettings = React.useMemo(() => {
    const settingsMap = settingsModule?.settingsMap;
    const buttonColorConfigMap = {
      1: {
        color: '#25091A',
        borderColor: '#B62A7E',
      },
      2: {
        color: settingsMap?.tertiaryColor,
        borderColor: settingsMap?.primaryColor,
      },
      3: {
        color: settingsMap?.tertiaryColor,
        borderColor: settingsMap?.primaryColor,
      },
    };
    return {
      buttonColor: buttonColorConfigMap[settingsModule?.id]?.color,
      buttonBorderColor: buttonColorConfigMap[settingsModule?.id]?.borderColor,
      primaryColor: settingsMap?.primaryColor,
      secondaryColor: settingsMap?.secondaryColor,
      empretecGraphics: settingsMap?.empretecGraphics,
      channelIdentifier: settingsMap?.channelIdentifier,
    };
  }, [settingsModule]);

  const mapStep = React.useMemo(() => {
    return {
      1: {
        textNormal: 'Entrevista de seleção',
        textResponsive: 'Entrevista',
        content:
          responserMethod === MapReponserMethod.WEBCHAT ? (
            <Chat
              moduleId={interview?.module.id}
              interview={interview}
              pageSettings={pageSettings}
            />
          ) : (
            <SelectionInterview
              interview={interview}
              pageSettings={pageSettings}
              answers={answers}
            />
          ),
      },
      2: {
        textNormal: 'Questionário de seleção',
        textResponsive: 'Questionário',
        content: (
          <QuestionSection interview={interview} pageSettings={pageSettings} />
        ),
      },
      3: {
        textNormal: 'Entrevista de seleção',
        textResponsive: 'Entrevista',
        content:
          responserMethod === MapReponserMethod.WEBCHAT ? (
            <Chat
              moduleId={interview?.module.id}
              interview={interview}
              pageSettings={pageSettings}
            />
          ) : (
            <SelectionInterview
              interview={interview}
              answers={answers}
              pageSettings={pageSettings}
            />
          ),
      },
    };
  }, [answers, interview, pageSettings, responserMethod]);

  const getContent = React.useCallback(
    (id) => {
      return mapStep[id].content;
    },
    [mapStep],
  );

  const getStepTitle = (id) => {
    return isLargeDisplay(breakpointController)
      ? mapStep[id].textNormal
      : mapStep[id].textResponsive;
  };
  return (
    <LayoutCandidate pageSettings={pageSettings} moduleId={settingsModule?.id}>
      {loading ? (
        <ContainerAboutCourse>
          <BaSeLoadingPage></BaSeLoadingPage>
        </ContainerAboutCourse>
      ) : (
        <ContainerAboutCourse empretecGraphics={pageSettings.empretecGraphics}>
          <BaSeStepper
            activeColor={BaSeTheme.colors.institucionais.azulSebrae75}
            incompletedColor={BaSeTheme.colors.institucionais.cinzaSebrae90}
            currentStep={currentStep}
            withTransition
            onStepChange={(step) => setCurrentStep(step)}
            steps={[
              {
                title: isLargeDisplay(breakpointController)
                  ? 'Conheça o Empretec'
                  : 'O Empretec',
                isClickable: false,
                content: () => (
                  <MeetEmpretec
                    setCurrentStep={setCurrentStep}
                    interview={interview}
                    pageSettings={pageSettings}
                  />
                ),
              },
              {
                title: 'Dados básicos',
                isClickable: false,
                content: () => (
                  <BasicData
                    setCurrentStep={setCurrentStep}
                    setAnswers={setAnswers}
                    interview={interview}
                    pageSettings={pageSettings}
                  />
                ),
              },
              {
                title: getStepTitle(interview?.module?.id),
                isClickable: false,
                content: () => getContent(interview?.module.id),
                // <SelectionInterview
                //   interview={interview}
                //   answers={answers}
                //   pageSettings={pageSettings}
                // />
              },
            ]}
          />
        </ContainerAboutCourse>
      )}
    </LayoutCandidate>
  );
};

async function setStep(
  result,
  getAnswersByInterview,
  setCurrentStep,
  setAnswers,
  navigateTo,
  hasRole,
) {
  const finalized = isFinalizedStateMap[result?.statusCompletingInterview];
  if (finalized && !hasRole('ADMINISTRADOR')) {
    navigateTo('/candidato/resultado', { keepQueryString: true });
    return;
  }

  getAnswersByInterview(result?.id).then((answersList) => {
    if (answersList.length > 0) {
      setAnswers(answersList);
    }
  });

  if (!result?.viewedVideo || !result?.acceptedTerm) {
    setCurrentStep(1);
    return;
  }

  if (
    result?.candidate.professionalSituation !== undefined &&
    result?.candidate.emergencyContact !== undefined
  ) {
    setCurrentStep(3);
    return;
  }

  const acceptUseConditions = result?.acceptedTerm && result?.viewedVideo;

  if ((!finalized || hasRole('ADMINISTRADOR')) && acceptUseConditions) {
    setCurrentStep(2);
    return;
  }

  // if ((!finalized || hasRole('ADMINISTRADOR')) && acceptUseConditions) {

  //   return;
  // }
  // (!finalized || hasRole('ADMINISTRADOR')) &&
  //   acceptUseConditions &&
  //   getAnswersByInterview(result?.id).then((answersList) => {
  //     if (answersList.length) {
  //       setCurrentStep(3);
  //       setAnswers(answersList);
  //     } else {
  //       setCurrentStep(2);
  //     }
  //   });
}

function isLargeDisplay(breakpointController) {
  return (
    breakpointController.inDesktop ||
    breakpointController.inDesktopLarge ||
    breakpointController.inTablet
  );
}
